import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft,
    Box,
    Calendar,
    MapPin,
    User as UserIcon,
    Hash,
    CheckCircle2,
    XCircle,
    FileText,
    Camera,
    Trash2,
    AlertCircle,
    Anchor,
    History,
    Edit3
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import { toSydneyDate } from '../../utils/helpers';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const LiftingInspectionDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [inspection, setInspection] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    useEffect(() => {
        const fetchInspection = async () => {
            try {
                setLoading(true);
                const result = await api.get(`/api/assets/lifting-inspections/${id}`);
                setInspection(result);
            } catch (err) {
                console.error(err);
                toast.error('Failed to fetch inspection details');
                navigate('/assets?tab=lifting');
            } finally {
                setLoading(false);
            }
        };
        fetchInspection();
    }, [id, navigate]);

    const handleDelete = async () => {
        try {
            await api.delete(`/api/assets/lifting-inspections/${id}`);
            toast.success('Inspection moved to recycle bin');
            navigate('/assets?tab=lifting');
        } catch (err) {
            console.error(err);
            toast.error('Failed to delete inspection');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!inspection) return null;

    return (
        <div className="max-w-4xl mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="icon"
                        className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                        onClick={() => navigate('/assets?tab=lifting')}
                    >
                        <ChevronLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                             Lifting Inspection Details
                        </h1>
                        <p className="text-gray-500 text-sm font-medium">
                            Viewing Gear & Sling record for {inspection.asset?.name}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        className="rounded-xl gap-2 border-slate-200 text-slate-700 hover:bg-slate-50 transition-all font-bold"
                        onClick={() => navigate(`/assets/lifting-inspections/${id}/edit`)}
                    >
                        <Edit3 size={18} />
                        Edit Record
                    </Button>
                    <Button
                        variant="outline"
                        className="rounded-xl gap-2 border-red-100 text-red-600 hover:bg-red-50 hover:border-red-200 transition-all font-bold"
                        onClick={() => setIsDeleteDialogOpen(true)}
                    >
                        <Trash2 size={18} />
                        Delete Record
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 1. Equipment & Location */}
                <div className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-50 rounded-xl">
                            <Box size={20} className="text-blue-500" />
                        </div>
                        <h2 className="font-bold text-gray-900 text-lg">Equipment Details</h2>
                    </div>

                    <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Name</span>
                            <p className="font-bold text-gray-900">{inspection.asset?.name || 'Deleted Asset'}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Tag</span>
                            <code className="bg-white px-2 py-1 rounded-md border border-gray-200 text-gray-700 font-bold text-xs">{inspection.asset?.tag || 'N/A'}</code>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <div className="flex items-center gap-2 mb-1">
                                <MapPin size={12} className="text-gray-400" />
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Location</span>
                            </div>
                            <p className="font-bold text-gray-900">{inspection.location}</p>
                        </div>
                    </div>
                </div>

                {/* 2. Inspection Context */}
                <div className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-slate-50 rounded-xl">
                            <UserIcon size={20} className="text-slate-500" />
                        </div>
                        <h2 className="font-bold text-gray-900 text-lg">Inspector Context</h2>
                    </div>

                    <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Tested By</span>
                            <p className="font-bold text-gray-900">{inspection.testedBy}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Licence Number</span>
                            <p className="font-bold text-gray-900">{inspection.licenceNo || 'Not provided'}</p>
                        </div>
                        <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100">
                            <div className="flex items-center gap-2 mb-1">
                                <Anchor size={12} className="text-amber-500" />
                                <span className="text-[10px] font-bold text-amber-500 uppercase tracking-widest">Next Action</span>
                            </div>
                            <p className="font-bold text-amber-700">{inspection.nextAction}</p>
                        </div>
                    </div>
                </div>

                {/* 3. Results & Scheduling */}
                <div className="md:col-span-2 bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-8">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-50 rounded-xl">
                                <FileText size={20} className="text-amber-500" />
                            </div>
                            <h2 className="font-bold text-gray-900 text-lg">Test Results & Scheduling</h2>
                        </div>
                        <Badge variant="outline" className={cn(
                            "rounded-xl border-0 font-black px-6 py-2 text-sm shadow-sm",
                            inspection.testResult === 'Pass' ? "bg-amber-50 text-amber-600 ring-1 ring-amber-100" : "bg-red-50 text-red-600 ring-1 ring-red-100"
                        )}>
                            {inspection.testResult?.toUpperCase()}
                        </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-4 p-5 bg-gray-50 rounded-[24px] border border-gray-100">
                            <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-blue-500">
                                <Calendar size={24} />
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">Test Date</span>
                                <p className="font-bold text-gray-900 text-lg">{toSydneyDate(inspection.testDate)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 p-5 bg-gray-50 rounded-[24px] border border-gray-100">
                            <div className="w-12 h-12 rounded-2xl bg-white border border-gray-100 flex items-center justify-center text-orange-500">
                                <History size={24} />
                            </div>
                            <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-0.5">Next Test Due</span>
                                <p className="font-bold text-gray-900 text-lg">{toSydneyDate(inspection.nextTestDue)}</p>
                            </div>
                        </div>
                    </div>

                    {inspection.comments && (
                        <div className="mt-6 p-6 bg-gray-50 rounded-[24px] border border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-2">Comments & Notes</span>
                            <p className="text-gray-700 leading-relaxed font-medium">
                                {inspection.comments}
                            </p>
                        </div>
                    )}

                    {inspection.photos && inspection.photos.length > 0 && (
                        <div className="mt-6">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-3">Photo Documentation</span>
                            <div className="flex flex-wrap gap-4">
                                {inspection.photos.map((photo: any, idx: number) => (
                                    <div key={idx} className="relative group w-full md:w-[calc(50%-8px)] rounded-2xl overflow-hidden shadow-sm border border-gray-100 bg-gray-50">
                                        <img
                                            src={photo.photoUrl}
                                            alt={`Evidence ${idx + 1}`}
                                            className="w-full h-auto object-cover max-h-[300px] hover:scale-105 transition-transform duration-500 cursor-pointer"
                                            onClick={() => window.open(photo.photoUrl, '_blank')}
                                        />
                                        <div className="absolute top-4 right-4 p-2 bg-black/40 backdrop-blur-sm rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <Camera size={16} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Delete Confirmation */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent className="rounded-3xl border-gray-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold">Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-500 font-medium">
                            This will move the lifting inspection record to the recycle bin. It will be kept for 180 days before permanent deletion.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2 sm:gap-0">
                        <AlertDialogCancel className="rounded-xl font-bold mr-2">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold"
                        >
                            Yes, delete record
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default LiftingInspectionDetails;
